import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import { Column } from 'react-table'
import { Button, Dropdown, message, Modal } from 'antd'
import type { MenuProps } from 'antd'
import * as Icons from '@ant-design/icons'

import { NextTableContext } from '@shared/modules/next-table/types'
import { nextTableColumns } from '@shared/modules/next-table/helpers/next-table-columns'
import { useSearchParams } from '@shared/hooks/use-search-params'

import { AppointmentsTableContext } from '../context'
import { Appointment } from '../types'
import { localURLMaker } from '@shared/utils/url-maker'
import { StopPropagation } from '@shared/components/stop-propagation'
import { AppointmentsService } from '../services'
import { useStatusList } from '../../status/hooks/use-list'
import { printAppointmentDocumentUseCase } from '../use-cases/print-document'
import { useAuth } from '@modules/me/hooks'
import { CashRegisterStatusContext } from '../../cash-register/context/status'
import { AppointmentRoutes } from '../constants'
import { useBackgroundNavigate } from '@shared/hooks'

export const useAppointmentsTable = ({ limited = false }) => {
  const { handleFetch } = useContext<NextTableContext>(AppointmentsTableContext)
  const navigate = useBackgroundNavigate()
  const statusList = useStatusList({ modelId: 13 })
  const { can } = useAuth()
  const cashRegisterStatus = useContext(CashRegisterStatusContext)

  const getRowProps = useCallback(
    (id) => ({
      onDoubleClick: () => navigate(localURLMaker(AppointmentRoutes.DETAILS, { id })),
    }),
    [navigate],
  )

  const columns = useMemo<Column<Appointment>[]>(
    () => [
      {
        ...nextTableColumns.actions,
        Cell: ({ row: { original } }) => {
          const goTo = useCallback(
            (type: string) => {
              navigate(localURLMaker(AppointmentRoutes.BY_TYPE, { id: original.id, type }))
            },
            [original.id],
          )

          const goToUpdate = useCallback(() => {
            navigate(
              localURLMaker(AppointmentRoutes.UPDATE, {
                id: original.id,
                userId: original.customer.id,
              }),
            )
          }, [original.customer.id, original.id])

          const handlePrintCheck = useCallback(async () => {
            if (!cashRegisterStatus.data?.connected) {
              message.error('Kassa əməliyyatları üçün ekranın yuxarı hissəsində olan "Obyekt seç" düyməsindən istifadə etməklə obyektinizi seçin')
              return false
            }

            const action = async () => {
              const result = await AppointmentsService.print(original.id)

              if (result.status === 200) {
                message.success('Çap edildi.')
              } else {
                message.error(result.data)
              }
            }

            Modal.confirm({
              title: 'Çap et',
              content: 'Qəbulu çap etməyə əminsinizmi?',
              onOk: action,
            })
          }, [original.id])

          const handlePrintDocument = useCallback(() => {
            return printAppointmentDocumentUseCase(original.id)
          }, [original.id])

          const handleAction = useCallback(
            async (actionType) => {
              const handler = async () => {
                message.loading({ key: 'message', content: 'Əməliyyat aparılır.' })
                const result = await AppointmentsService.actionByIds([original.id], actionType)

                if (result.status === 200) {
                  message.success({ key: 'message', content: 'Əməliyyat müvəffəqiyyətlə başa çatdı' })
                  navigate(localURLMaker(AppointmentRoutes.INDEX, {}, { reFetchAppointmentsTable: true }))
                } else {
                  message.error({ key: 'message', content: result.data as string })
                }
              }

              Modal.confirm({
                title: 'Əməliyyatı etməyinizə əminsinizmi?',
                content: 'Əməliyyatı etdikdən sonra məlumatların geri qaytarılması mümkün olmayacaq.',
                onOk: handler,
              })
            },
            [original.id],
          )

          const handleUpdateState = useCallback(
            async (stateId: number | string) => {
              const handler = async () => {
                message.loading({ key: 'message', content: 'Əməliyyat aparılır.' })
                const result = await AppointmentsService.updateStatusByIds([original.id], stateId)

                if (result.status === 200) {
                  message.success({ key: 'message', content: 'Əməliyyat müvəffəqiyyətlə başa çatdı' })
                  navigate(localURLMaker(AppointmentRoutes.INDEX, {}, { reFetchAppointmentsTable: true }))
                } else {
                  message.error({ key: 'message', content: result.data as string })
                }
              }

              Modal.confirm({
                title: 'Status dəyişməyə əminsinizmi?',
                content: 'Status dəyişdikdən sonra məlumatların geri qaytarılması mümkün olmayacaq.',
                onOk: handler,
              })
            },
            [original.id],
          )

          const menuItems: MenuProps['items'] = [
            can(64) && {
              key: 'details',
              label: 'Ətraflı',
              icon: <Icons.FileTextOutlined />,
              onClick: () => goTo('details'),
            },
            can(66) && {
              key: 'update',
              label: 'Dəyişdir',
              icon: <Icons.EditOutlined />,
              onClick: goToUpdate,
            },
            { type: 'divider' },
            can(90) && {
              key: 'print-check',
              label: 'Çeki çap et',
              icon: <Icons.PrinterOutlined />,
              disabled: original.status.id !== 12,
              onClick: handlePrintCheck,
            },
            can(70) && {
              key: 'print-document',
              label: 'Qəbul sənədini çap et',
              icon: <Icons.PrinterOutlined />,
              onClick: handlePrintDocument,
            },
            { type: 'divider' },
            can(68) && {
              key: 'status-change',
              label: 'Status dəyiş',
              icon: <Icons.AppstoreOutlined />,
              children: statusList.data.map((status) => ({
                key: `status-${status.id}`,
                label: status.name,
                onClick: () => handleUpdateState(status.id),
              })),
            },
            { type: 'divider' },
            can(69) && {
              key: 'remove',
              label: 'Sil',
              icon: <Icons.DeleteOutlined />,
              onClick: () => handleAction('remove'),
            },
          ].filter(Boolean) as MenuProps['items']

          return (
            <StopPropagation>
              <Dropdown menu={{ items: menuItems }}>
                <Button size='small' icon={<Icons.MoreOutlined />} />
              </Dropdown>
            </StopPropagation>
          )
        },
      },
      {
        ...nextTableColumns.smaller,
        accessor: (row) => row.id,
        id: 'id',
        Header: 'Kod',
      },
      {
        ...nextTableColumns.small,
        accessor: (row) => row.customer.id,
        sortable: !limited,
        filterable: !limited,
        id: 'client_id',
        Header: 'Müştəri kodu',
      },
      {
        accessor: (row) => row.customer.fullName,
        sortable: !limited,
        filterable: !limited,
        id: 'client_name',
        Header: 'Müştəri',
      },
      {
        ...nextTableColumns.smaller,
        accessor: (row) => row.customer.gender,
        id: 'gender',
        Header: 'Cins',
        Cell: ({ cell: { value } }) => (value === 'female' ? 'Qadın' : value === 'male' ? 'Kişi' : null),
      },
      {
        accessor: (row) => row.customer.phoneNumber,
        id: 'number',
        Header: 'Telefon nömrəsi',
      },
      {
        ...nextTableColumns.normal,
        accessor: (row) => row.status.name,
        id: 'state_name',
        Header: 'Status',
      },
      {
        ...nextTableColumns.small,
        accessor: (row) => row.type,
        id: 'type',
        Header: 'Tipi',
        Cell: ({ cell: { value } }) => (value === 'outpatient' ? 'Ambulator' : 'Göndəriş'),
      },
      {
        ...nextTableColumns.date,
        accessor: (row) => row.scheduledAt,
        id: 'visit_date',
        Header: 'Qəbul tarixi',
      },
      {
        ...nextTableColumns.date,
        accessor: (row) => row.createdAt,
        id: 'created_at',
        Header: 'Yaranma tarixi',
      },
    ],
    [can, cashRegisterStatus.data?.connected, navigate, limited, statusList.data],
  )

  const { searchParams, remove } = useSearchParams<{ reFetchAppointmentsTable?: string }>()

  useEffect(() => {
    (async () => {
      if (searchParams.reFetchAppointmentsTable) {
        remove.current('reFetchAppointmentsTable')
        await handleFetch()
      }
    })()
  }, [handleFetch, remove, searchParams.reFetchAppointmentsTable])

  useEffect(() => {
    handleFetch()
  }, [handleFetch])

  return { columns, getRowProps }
}
